import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { buildSportsVodPosterUrl } from '@/services/sportsVodApi';
import type { SportsVodTitle } from '@/types/sportsVod';

interface Props {
  title: SportsVodTitle;
}

function formatPostedAt(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export function SportsVodCard({ title }: Props): JSX.Element {
  const navigate = useNavigate();
  const { t } = useTranslation();
  // Poster fetches can 4xx if upstream pulled the image — falling back to a
  // placeholder keeps the grid from collapsing into broken-image icons.
  const [posterFailed, setPosterFailed] = useState(false);
  const postedLabel = formatPostedAt(title.postedAt);

  function open(): void {
    navigate(`/watch/sports-vod/${encodeURIComponent(title.id)}?source=${encodeURIComponent(title.source)}`);
  }

  return (
    <button
      type="button"
      className="sports-card sports-vod-card"
      onClick={open}
      aria-label={t('sports.watchAria', { title: title.title })}
      data-testid={`sports-vod-card-${title.id}`}
    >
      <div className="sports-card__poster">
        {title.posterUrl !== null && !posterFailed ? (
          <img
            src={buildSportsVodPosterUrl(title.id)}
            alt=""
            loading="lazy"
            onError={() => setPosterFailed(true)}
          />
        ) : (
          <div className="sports-card__poster-placeholder" />
        )}
        <span className="time-pill">{title.sport}</span>
      </div>
      <div className="sports-card__title">{title.title}</div>
      <div className="sports-card__meta">
        {postedLabel && <span>{postedLabel}</span>}
        {postedLabel && <span aria-hidden="true"> · </span>}
        <span className="sports-vod-card__source">{title.source}</span>
      </div>
    </button>
  );
}
